import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton, SecondaryButton, SnackbarStatus, StatusBar, Typography } from '@weareopenr/component-library';
import ModalImg from 'src/assets/images/create-menu-modal.png';
import ModalWrapper from 'src/components/ModalWrapper';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import {
  MENU_SET_DETAILS_LABELS,
  MenuDetailsFields,
  UNIQUE_MENU_SET_NAME_ERROR as UNIQUE_NAME_ERROR,
} from 'src/components/MenuDetailsFields';
import { IFormData } from 'src/components/MenuModal/types';
import { ICreateMenuSetRequest } from 'src/api/createMenuSet';
import { EntitySource } from 'src/constants';
import { useCreateMenuSet } from 'src/hooks/useCreateMenuSet';
import { useMenus } from 'src/hooks/useMenus';
import { useMenuLabels } from 'src/hooks/useMenuLabels';
import { useMenuSets } from 'src/hooks/useMenuSets';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { Routes } from 'src/routes';
import { getCompatibleMenus } from 'src/utils/menuSets/getCompatibleMenus';
import { toMenuLabelsPayload } from 'src/utils/menuLabels/menuLabels';
import { MenuSetMenusStep } from './MenuSetMenusStep';
import styles from './CreateMenuSetModal.module.scss';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

const QUICK_NOTE = (
  <>
    <strong>Quick note -</strong> By creating a Menu Set, you are able to stack menus together. Upon publishing this
    Menu Set, the above image and consumer name will be used.
  </>
);

enum CreateMenuSetStep {
  Details = 0,
  Menus = 1,
}

interface ICreateMenuSetModalProps {
  open: boolean;
  closeModal: () => void;
}

const CreateMenuSetFlow = ({ closeModal }: Pick<ICreateMenuSetModalProps, 'closeModal'>): ReactElement => {
  const navigate = useNavigate();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { mutateAsync: createMenuSetMutation } = useCreateMenuSet((menuSet) => {
    closeModal();
    navigate(`${Routes.MENU_SETS}/${menuSet.menu_set_id}`);
  });
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { data: ranges } = useRanges({ enabled: rangesEnabled });
  const { data: menus, isLoading: isMenusLoading } = useMenus();
  const { data: menuLabels, isLoading: isMenuLabelsLoading } = useMenuLabels();
  const { data: menuSetsData } = useMenuSets(50, undefined, undefined, undefined, {
    staleTime: EXISTING_NAMES_STALE_TIME,
  });
  const existingMenuSetNames = useMemo(
    () => menuSetsData?.pages.flatMap((page) => page.data.map((menuSet) => menuSet.name.toLocaleLowerCase())) ?? [],
    [menuSetsData],
  );

  const [step, setStep] = useState<CreateMenuSetStep>(CreateMenuSetStep.Details);
  const [image, setImage] = useState<File | string | null>(null);
  const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([]);

  const form = useForm<IFormData>({
    defaultValues: {
      name: '',
      consumerName: '',
      files: null,
      source: EntitySource.LIBRARY,
      ranges: [],
      sourceMenuLabels: [],
      menuLabels: [],
    },
  });
  const {
    handleSubmit,
    trigger,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;
  const rawName = watch('name');
  const name = rawName.trim();
  const source = watch('source');
  const rangeIds = watch('ranges');
  const sourceMenuLabelIds = watch('sourceMenuLabels') ?? [];
  const isNameTaken = existingMenuSetNames.includes(name.toLocaleLowerCase());

  useEffect(() => {
    if (isNameTaken) {
      setError('name', { type: 'unique', message: UNIQUE_NAME_ERROR });
    } else if (errors.name?.type === 'unique') {
      clearErrors('name');
    }
  }, [rawName, isNameTaken]);

  const compatibleMenus = useMemo(
    () =>
      getCompatibleMenus(
        menus ?? [],
        source as EntitySource,
        source === EntitySource.MENU_LABELS ? sourceMenuLabelIds : rangeIds,
      ),
    [menus, source, rangeIds, sourceMenuLabelIds],
  );
  // Menus picked before the source changed may no longer fit the set, so only compatible ones are kept.
  // Kept in list order, which is the order the menus are added to the set in.
  const menuIdsToAdd = useMemo(
    () => compatibleMenus.filter(({ menuId }) => selectedMenuIds.includes(menuId)).map(({ menuId }) => menuId),
    [compatibleMenus, selectedMenuIds],
  );

  const goToMenusStep = async () => {
    // Validating the name would clear the uniqueness error, so a taken name is stopped first.
    if (isNameTaken) {
      setError('name', { type: 'unique', message: UNIQUE_NAME_ERROR });
      return;
    }
    const isNameValid = await trigger('name');
    if (source === EntitySource.RANGES && rangeIds.length === 0) {
      setError('ranges', { message: 'Must select one or more ranges before you can continue' });
      return;
    }
    if (source === EntitySource.MENU_LABELS && sourceMenuLabelIds.length === 0) {
      setError('sourceMenuLabels', { message: 'Must select one or more menu labels before you can continue' });
      return;
    }
    if (isNameValid) setStep(CreateMenuSetStep.Menus);
  };

  const onSubmit = async (data: IFormData) => {
    clearError();
    const payload: ICreateMenuSetRequest = {
      name: data.name.trim(),
      menus: JSON.stringify(menuIdsToAdd),
      ...(data.consumerName.trim() && { consumer_name: data.consumerName.trim() }),
      ...(data.source === EntitySource.RANGES && { ranges: JSON.stringify(data.ranges) }),
      ...(data.source === EntitySource.MENU_LABELS && {
        source_menu_labels: JSON.stringify(data.sourceMenuLabels ?? []),
      }),
      ...(data.menuLabels.length > 0 && { menu_labels: toMenuLabelsPayload(data.menuLabels) }),
      ...(image instanceof File && { files: image }),
    };

    try {
      await createMenuSetMutation(payload);
    } catch (e) {
      if (e instanceof Error && e.message === UNIQUE_NAME_ERROR) {
        setStep(CreateMenuSetStep.Details);
        setError('name', { message: UNIQUE_NAME_ERROR });
        return;
      }
      showNotification({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst creating the menu set' });
    }
  };

  const isDetailsStep = step === CreateMenuSetStep.Details;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['form']}>
      <div className={styles['header']}>
        <Typography weight="bold" size="medium" className={styles['step']}>
          {`Step ${step + 1} of 2`}
        </Typography>
        <Typography size="xxlarge" weight="semi-bold" className={styles['title']}>
          {isDetailsStep ? 'Create Menu Set' : `Add menu(s) to ${name}`}
        </Typography>
      </div>

      <div className={styles['content']}>
        {isDetailsStep ? (
          <>
            <MenuDetailsFields
              form={form}
              labels={MENU_SET_DETAILS_LABELS}
              image={image}
              setImage={setImage}
              showSource
              menuSetSource={{ showRanges: rangesEnabled }}
              ranges={ranges}
              showMenuLabels
              menuLabels={menuLabels}
              isMenuLabelsLoading={isMenuLabelsLoading}
            />
            <StatusBar variant="info" message={QUICK_NOTE} />
          </>
        ) : (
          <MenuSetMenusStep
            menus={compatibleMenus}
            selectedMenuIds={menuIdsToAdd}
            onMenusChange={setSelectedMenuIds}
            isLoading={isMenusLoading}
            sourceMenuLabelIds={source === EntitySource.MENU_LABELS ? sourceMenuLabelIds : undefined}
          />
        )}
      </div>

      <div className={styles['footer']}>
        <ButtonContainer
          margin="none"
          left={
            <SecondaryButton
              buttonStyle="outline"
              type="button"
              size="full-width"
              onClick={isDetailsStep ? closeModal : () => setStep(CreateMenuSetStep.Details)}
            >
              {isDetailsStep ? 'Cancel' : 'Back'}
            </SecondaryButton>
          }
          right={
            isDetailsStep ? (
              <PrimaryButton key="continue" type="button" size="full-width" onClick={goToMenusStep}>
                Continue
              </PrimaryButton>
            ) : (
              <PrimaryButton
                key="create"
                type="submit"
                size="full-width"
                loading={isSubmitting}
                disabled={menuIdsToAdd.length === 0 || isSubmitting}
              >
                Create Menu Set
              </PrimaryButton>
            )
          }
        />
        {errorMessage && (
          <div className={styles['error-banner']}>
            <StatusBar message={errorMessage} variant="error" />
          </div>
        )}
      </div>
    </form>
  );
};

export const CreateMenuSetModal = ({ open, closeModal }: ICreateMenuSetModalProps): ReactElement => (
  <ModalWrapper closeModal={closeModal} open={open} image={ModalImg} imageAlt="Picture of menus">
    <CreateMenuSetFlow closeModal={closeModal} />
  </ModalWrapper>
);

export default CreateMenuSetModal;
